<template>
  <div>
    <div v-if="!loadingIncident && recovery">
      <page-header>
        <template #title>
          <h1 class="page-title">
            Recovery - {{ claimReference }}
            <VulnerabilityLabel v-if="hasVulnerabilities"></VulnerabilityLabel>
          </h1>
        </template>
        <template #actions>
          <incident-download-buttons
            :incident="incident"
            class="btn-group btn-group-sm"
          ></incident-download-buttons>
          <page-header-action
            @click.prevent="moveToClaimMonitoring"
            v-if="!readonly"
          >
            Move To Claim Monitoring
          </page-header-action>
          <page-header-action
            :tag="canSendToVerify ? 'router-link' : 'button'"
            :to="canSendToVerify ? sendToVerifyPath : undefined"
            :disabled="!canSendToVerify || undefined"
            :title="
              !canSendToVerify
                ? 'The Verify integration must be enabled for the TPI to be able to send to Verify'
                : undefined
            "
            v-if="!readonly"
          >
            Send to Verify
          </page-header-action>
          <page-header-action @click.prevent="toggleNotes">
            Notes
          </page-header-action>
          <page-header-action
            variant="danger"
            @click.prevent="closeCase"
            v-if="!readonly"
          >
            Close Case
          </page-header-action>
        </template>
      </page-header>

      <claim-handler></claim-handler>

      <div class="card">
        <incident-tabs
          :tabs="tabs"
          :source="source"
          :incident="incident"
          :module="module"
          v-on:selected="setActive"
          :active="currentView"
        ></incident-tabs>

        <transition-group name="component-fade" mode="out-in">
          <recovery
            :incident="incident"
            :module="module"
            :recovery="recovery"
            :readonly="readonly"
            v-show="currentView == 'recovery'"
            v-if="hasPermission('claims.recovery.figures') || readonly"
            key="recovery"
          ></recovery>

          <incident-diary
            :url="'/recovery/' + incident"
            :incident="incident"
            :readonly="readonly"
            :slim="true"
            key="diary"
            v-if="currentView == 'diary'"
          ></incident-diary>

          <incident-source
            :incident="incident"
            :showCancel="showCancel"
            :readonly="readonly"
            v-if="currentView == 'source'"
            v-on:unsaved-data="saveStatus"
            key="source"
          ></incident-source>

          <accident-details
            :incident="incident"
            :readonly="readonly"
            v-if="currentView == 'accident_description'"
            v-on:unsaved-data="saveStatus"
            key="accidentDetails"
          ></accident-details>

          <client
            :incident="incident"
            :module="module"
            :readonly="readonly"
            v-if="currentView == 'client'"
            v-on:unsaved-data="saveStatus"
            key="client"
          ></client>

          <third-parties
            :incident="incident"
            :module="module"
            :readonly="readonly"
            v-if="currentView == 'third_parties'"
            v-on:unsaved-data="saveStatus"
            key="thirdParties"
          ></third-parties>

          <witnesses
            :incident="incident"
            :readonly="readonly"
            v-if="currentView == 'witnesses'"
            key="witnesses"
          ></witnesses>

          <statements
            :incident="incident"
            :readonly="readonly"
            v-if="currentView == 'statements'"
            key="statements"
            v-on:unsaved-data="saveStatus"
          ></statements>

          <documents
            :incident="incident"
            :readonly="readonly"
            v-if="currentView === 'documents'"
            key="documents"
          ></documents>

          <jobs
            :incident="source.incident_id"
            v-if="currentView == 'jobs'"
            key="jobs"
          ></jobs>

          <email
            :incident="incident"
            v-if="currentView === 'email'"
            key="email"
          ></email>
        </transition-group>
      </div>
    </div>
    <div v-else>
      <preloader></preloader>
    </div>

    <notes-window
      :show="notes"
      :incident="incident"
      v-on:toggle-note="toggleNotes"
    ></notes-window>
  </div>
</template>

<script>
import Recovery from './Sections/Recovery.vue';
import PaymentPack from './Sections/PaymentPack.vue';
import IncidentDiary from './../Shared/IncidentDiary.vue';
import Source from './../FirstNoticeOfLoss/Sections/Source.vue';
import AccidentDetails from './../FirstNoticeOfLoss/Sections/AccidentDetails.vue';
import Client from './../FirstNoticeOfLoss/Sections/Client.vue';
import ThirdParties from './../FirstNoticeOfLoss/Sections/ThirdParties.vue';
import Witnesses from './../FirstNoticeOfLoss/Sections/Witnesses.vue';
import Statements from './../FirstNoticeOfLoss/Sections/Statements.vue';
import Documents from './../Shared/Documents.vue';
import Email from './../Shared/Email.vue';
import Notes from './../Shared/Notes.vue';
import Hires from './../HireMonitoring/Sections/Hires';
import IncidentTabs from '../Shared/IncidentTabs';
import IncidentDownloadButtons from '../Shared/IncidentDownloadButtons';
import Jobs from './../FirstNoticeOfLoss/Sections/Jobs';
import VulnerabilityLabel from '@/components/Modules/Shared/VulnerabilityLabel.vue';
import PageHeader from './../Shared/PageHeader.vue';
import PageHeaderAction from './../Shared/PageHeaderAction.vue';

export default {
  props: {
    incident: {
      type: String,
      required: true
    }
  },

  components: {
    VulnerabilityLabel,
    PageHeader,
    PageHeaderAction,
    'notes-window': Notes,
    recovery: Recovery,
    // 'payment-pack': PaymentPack,
    Documents,
    IncidentDiary,
    'incident-source': Source,
    AccidentDetails,
    client: Client,
    ThirdParties,
    Witnesses,
    Statements,
    Email,
    IncidentTabs,
    IncidentDownloadButtons,
    Jobs
  },

  data() {
    return {
      module: 'Recovery',
      referringToAgent: false,
      settingStage: false,
      type: null,
      notes: false,
      unsaved_data: false,
      showCancel: false,
      downloading: false,
      currentView: null,
      hire_monitoring: {},
      form: new window.Form({}),
      loaded: false,
      deductions: []
    };
  },

  computed: {
    readonly() {
      return this.isReadonly();
    },

    tabs() {
      let tabs = [];

      if (this.hasPermission('claims.recovery.figures') || this.readonly) {
        tabs.push({
          name: 'recovery',
          label: 'Recovery'
        });
      }

      tabs.push({
        name: 'diary',
        label: 'Diary',
        disabled: false
      });

      tabs.push({
        name: 'jobs',
        label: 'Jobs',
        disabled: false
      });

      return tabs;
    },

    hasStage() {
      return this.recovery && this.recovery.status;
    },

    recovery() {
      return this.$store.getters['DebtRecovery/get'];
    },

    claimReference() {
      const source = this.$store.getters['Incident/source'];

      if (source && source.claim_reference) {
        return source.claim_reference;
      }

      return null;
    },

    source() {
      return this.$store.getters['Incident/source'];
    },

    loadingIncident() {
      return this.$store.getters['Incident/loading'];
    },

    canSendToVerify() {
      const thirdParties = this.$store.getters['Incident/thirdParties'];
      return thirdParties?.some(tp => tp.insurer?.verify_code);
    },

    sendToVerifyPath() {
      return `/recovery/${this.incident}/send-to-verify`;
    },

    hasVulnerabilities() {
      const vulnerabilities =
        this.$store.getters['Incident/vulnerabilities'] || [];

      return vulnerabilities.length > 0;
    }
  },

  mounted() {
    this.$store.dispatch('Incident/retrieve', this.incident);
    this.$store.commit('DebtRecovery/clearRecord');
    this.$store.commit('DebtRecoveryStatuses/retrieve');
    this.$store.commit('HoldBackReasons/retrieve');
    this.$store.commit('OutstandingClaimReasons/retrieve');
    this.$store.commit('ReasonNotIssued/retrieve');
    this.$store.commit('RateGroups/retrieve');
    this.$store.commit('ShortFallReasons/retrieve');
    this.$store.commit('Tariffs/retrieve');

    window.Ajax.get(`/debt-recovery/${this.incident}`)
      .then(response => {
        this.$store.commit('DebtRecovery/set', response.data);
      })
      .catch(error => {});

    this.currentView = this.tabs[0].name;
  },

  methods: {
    referToAgentCheck() {
      window
        .swal({
          title: 'Are you sure?',
          text: `This will instruct the agent to manage this case for you`,
          type: 'warning',
          showCancelButton: true
        })
        .then(() => {
          let agent = this.$store.getters.agent;
          if (agent) {
            this.referringToAgent = true;
            window.Ajax.post(
              `/incidents/${this.incident}/instruct-agent/${agent.identifier}`
            )
              .then(response => {
                window.Ajax.post(
                  `/first-notice-of-loss/${this.incident}/complete`
                )
                  .then(response => {
                    window.toastify.success(
                      'Success!',
                      'The agent was instructed successfully!'
                    );
                    this.$router.push('/recovery');
                    this.referringToAgent = false;
                  })
                  .catch(error => {
                    this.referringToAgent = false;
                    window.toastify.error(
                      'Error',
                      "Sorry, we couldn't instruct the agent"
                    );
                  });
              })
              .catch(error => {
                this.referringToAgent = false;
                window.toastify.error(
                  'Error',
                  "Sorry, we couldn't instruct the agent"
                );
              });
          }
        })
        .catch(error => {});
    },

    closeCase() {
      if (this.readonly) return;
      window
        .swal({
          title: 'Are you sure?',
          text: 'This will close the case',
          type: 'warning',
          showCancelButton: true,
          confirmButtonText: 'Close The Case',
          cancelButtonText: 'Do Not Close The Case'
        })
        .then(
          () => {
            window.Ajax.post(`/incidents/${this.incident}/close`)
              .then(response => {
                window.toastify.success(
                  'Success!',
                  'The case was closed successfully!'
                );
                this.$router.push('/recovery');
              })
              .catch(error => {});
          },
          dismiss => {}
        );
    },

    complete() {
      window.Ajax.post(`/recovery/${this.incident}/stage`)
        .then(response => {
          this.recovery = response.data;
        })
        .catch(error => {});
    },

    saveStatus(event) {
      this.unsaved_data = event;
    },

    saveWarning(tab) {
      window
        .swal({
          title: 'Are you sure?',
          text:
            'You will lose all details entered in this section if you continue!',
          type: 'warning',
          showCancelButton: true,
          confirmButtonText: 'Continue without saving',
          cancelButtonText: 'Cancel and stay in this section'
        })
        .then(
          () => {
            // Go to selected tab discarding changes
            this.currentView = tab;
            this.unsaved_data = false;
          },
          dismiss => {}
        );
    },

    setActive(tab) {
      // check if unsaved data on the current form
      if (this.unsaved_data && this.currentView !== tab) {
        this.saveWarning(tab); // show alert if there is
      } else {
        this.currentView = tab;
      }
    },

    toggleNotes() {
      this.notes = !this.notes;
    },

    moveToClaimMonitoring() {
      // If the user cannot update a claim then just redirect to claim monitoring
      if (!this.hasPermission('claims.update')) {
        this.route(`/claim-monitoring/${this.incident}`);
        return;
      }

      window.Ajax.post(`/incidents/${this.incident}/claim-monitoring`, {})
        .then(() => {
          window.toastify.success(
            'Success!',
            'The case was moved to claim monitoring successfully!'
          );
          this.$router.push(`/claim-monitoring/${this.incident}`);
        })
        .catch(() => {
          window.toastify.error(
            'An Error Occurred',
            'An error occurred while move the case to claim monitoring'
          );
        });
    }
  },

  beforeDestroy() {
    this.$store.commit('Incident/clearRecord');
  }
};
</script>
<style scoped>
.details-row {
  display: flex;
  justify-content: space-between;
}

.details-row .panel {
  width: 23%;
}
</style>
